import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  structuredData?: object;
}

const defaultTitle = 'MSP Vergelijker | Vind de beste IT-partner';
const defaultDescription = "Vergelijk MSP's in Nederland en vind de beste IT-partner voor jouw bedrijf. Onafhankelijk, gratis en overzichtelijk.";
const defaultOgImage = '/og-default.png';
const siteUrl = 'https://msp-vergelijker.nl';

export function SEOHead({
  title,
  description = defaultDescription,
  canonicalUrl,
  ogImage = defaultOgImage,
  type = 'website',
  noindex = false,
  structuredData,
}: SEOHeadProps) {
  const fullTitle = title || defaultTitle;
  // Never emit an empty description / og:description, even when a caller passes
  // an empty or whitespace-only string (e.g. missing CMS meta_description).
  const safeDescription = description?.trim() ? description.trim() : defaultDescription;
  const normalizedCanonicalPath = canonicalUrl
    ? (() => {
        let path = canonicalUrl.trim();
        if (!path.startsWith('/')) path = `/${path}`;
        // Strip query string and hash from canonical
        path = path.split('?')[0].split('#')[0];
        // Strip trailing slash unless it's the root
        if (path.length > 1 && path.endsWith('/')) path = path.replace(/\/+$/, '');
        return path;
      })()
    : undefined;
  const fullCanonicalUrl = normalizedCanonicalPath ? `${siteUrl}${normalizedCanonicalPath}` : undefined;
  // og:url must always be route-specific. Fall back to current pathname when
  // no explicit canonical is provided (SSR-safe via typeof window guard).
  // NOTE: This only updates document.head client-side. JS-less social crawlers
  // (LinkedIn, Slack, Facebook, WhatsApp) read the static index.html fallback
  // — accurate per-route social previews require prerendering/SSR.
  const fallbackPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const fullOgUrl = fullCanonicalUrl ?? `${siteUrl}${fallbackPath}`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  // The static SEO/social fallback tags in index.html exist for JS-less crawlers.
  // Once this component renders route-specific tags, the static duplicates are
  // removed so the rendered <head> holds exactly one description / og:image /
  // twitter:image per page. The raw HTML response keeps the fallback intact.
  useEffect(() => {
    document.head
      .querySelectorAll('meta[data-static-seo]')
      .forEach((el) => el.parentNode?.removeChild(el));
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={safeDescription} />
      {noindex ? <meta name="robots" content="noindex, follow" /> : <meta name="robots" content="index, follow" />}
      
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:url" content={fullOgUrl} />
      <meta property="og:site_name" content="MSP-Vergelijker.nl" />
      <meta property="og:locale" content="nl_NL" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content="@MSPVergelijker" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
